import { type FormEvent, useState } from 'react'

interface InitialPasswordChangeProps {
  accessToken: string
  fullName: string
  onSignOut: () => Promise<void>
}

interface ApiError {
  error?: string
}

export function InitialPasswordChange({
  accessToken,
  fullName,
  onSignOut,
}: InitialPasswordChangeProps) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)

    if (newPassword !== confirmation) {
      setError('A confirmação da nova senha não confere.')
      return
    }
    if (newPassword === currentPassword) {
      setError('A nova senha deve ser diferente da senha inicial.')
      return
    }

    setSubmitting(true)
    try {
      const result = await fetch('/api/change-password', {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmation,
        }),
      })
      const payload = await result.json() as ApiError
      if (!result.ok) throw new Error(payload.error || 'Não foi possível alterar a senha.')

      setCurrentPassword('')
      setNewPassword('')
      setConfirmation('')
      setCompleted(true)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Não foi possível alterar a senha.')
    } finally {
      setSubmitting(false)
    }
  }

  if (completed) {
    return (
      <main className="password-change-page">
        <section className="password-change-card" aria-labelledby="password-changed-title">
          <span className="password-change-mark">hvm</span>
          <p className="eyebrow">Senha atualizada</p>
          <h1 id="password-changed-title">Alteração concluída.</h1>
          <p>
            Sua senha inicial foi substituída. Entre novamente usando a nova senha.
          </p>
          <button className="primary-button" type="button" onClick={() => void onSignOut()}>
            Entrar novamente
          </button>
        </section>
      </main>
    )
  }

  return (
    <main className="password-change-page">
      <section className="password-change-card" aria-labelledby="password-change-title">
        <span className="password-change-mark">hvm</span>
        <p className="eyebrow">Primeiro acesso</p>
        <h1 id="password-change-title">Crie sua senha pessoal.</h1>
        <p>
          Olá, {fullName}. Por segurança, substitua a senha inicial antes de acessar
          os indicadores.
        </p>

        <form className="password-change-form" onSubmit={handleSubmit}>
          <label>
            Senha atual
            <input
              autoComplete="current-password"
              minLength={10}
              onChange={event => setCurrentPassword(event.target.value)}
              required
              type="password"
              value={currentPassword}
            />
          </label>
          <label>
            Nova senha
            <input
              autoComplete="new-password"
              minLength={10}
              onChange={event => setNewPassword(event.target.value)}
              required
              type="password"
              value={newPassword}
            />
          </label>
          <label>
            Confirmar nova senha
            <input
              autoComplete="new-password"
              minLength={10}
              onChange={event => setConfirmation(event.target.value)}
              required
              type="password"
              value={confirmation}
            />
          </label>

          {error && <p className="form-error" role="alert">{error}</p>}

          <button className="primary-button" disabled={submitting} type="submit">
            {submitting ? 'Alterando…' : 'Salvar nova senha'}
          </button>
          <button className="password-change-exit" type="button" onClick={() => void onSignOut()}>
            Sair
          </button>
        </form>
      </section>
    </main>
  )
}
